import React, { useEffect, useState, useRef } from "react";
import { MapContainer, useMap } from "react-leaflet";
import L from "leaflet";
import { getData } from "../../../../../Services/Services";
import { getQueriesListByWidgetType } from "../../../../../Services/EndPoints";

const Map = ({ 
  item, 
  selectedBankIds, 
  selectedBranchIds, 
  uploadedIcons, 
  iconMap, 
  iconSizes,
  attachmentList
}) => {
  console.log("map item attachmentList", attachmentList);
  console.log("map item", item);
  console.log("mapselectedBankIds", selectedBankIds);
  console.log("mapselectedBranchIds", selectedBranchIds);
  
  // State to store processed icons
  const [widgetIcons, setWidgetIcons] = useState({});
  const blobUrlsRef = useRef(new Set());

  // State for NiFi data filtering
  const [mapData, setMapData] = useState(item.data || { points: [] });
  const [loading, setLoading] = useState(false);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // Helper function to validate and clean map data points
  const validateMapPoints = (points) => {
    if (!Array.isArray(points)) {
      console.warn("Map: Points is not an array, returning empty array");
      return [];
    }

    const validPoints = points.filter(point => {
      // Check if point has valid lat/lng values
      const hasValidLat = point.lat !== undefined && point.lat !== null && !isNaN(parseFloat(point.lat));
      const hasValidLng = point.lng !== undefined && point.lng !== null && !isNaN(parseFloat(point.lng));
      
      if (!hasValidLat || !hasValidLng) {
        console.warn("Map: Filtering out invalid point:", point);
        return false;
      }

      // Convert to numbers and validate range
      const lat = parseFloat(point.lat);
      const lng = parseFloat(point.lng);
      
      const isValidLatRange = lat >= -90 && lat <= 90;
      const isValidLngRange = lng >= -180 && lng <= 180;
      
      if (!isValidLatRange || !isValidLngRange) {
        console.warn("Map: Filtering out point with invalid coordinates:", { lat, lng });
        return false;
      }

      return true;
    });

    console.log(`Map: Validated ${validPoints.length}/${points.length} points`);
    return validPoints;
  };

  // Get widget ID
  const getWidgetId = () => {
    return item?.widgetId || item?.id;
  };

  // Create widget-specific key for icons
  const getWidgetIconKey = (serviceName) => {
    const widgetId = getWidgetId();
    return widgetId ? `widget_${widgetId}_service_${serviceName}` : null;
  };

  // Clean up function for blob URLs
  const cleanupBlobUrls = (urlsToCleanup = []) => {
    urlsToCleanup.forEach(url => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
        blobUrlsRef.current.delete(url);
      }
    });
  };

  // Process icons from attachments
  useEffect(() => {
    const widgetId = getWidgetId();
    if (!widgetId) {
      setWidgetIcons({});
      return;
    }

    // Clean up existing blob URLs
    cleanupBlobUrls(Object.values(widgetIcons));
    
    const newWidgetIcons = {};
    const newBlobUrls = new Set();
    
    if (attachmentList && attachmentList.length > 0) {
      // Filter attachments for this specific widget
      const widgetAttachments = attachmentList.filter(attachment => 
        attachment.type === "MAP" && 
        attachment.widgetId === widgetId && 
        attachment.serviceName &&
        !attachment._markedForDeletion
      );

      // Process each attachment
      widgetAttachments.forEach(attachment => {
        const iconKey = getWidgetIconKey(attachment.serviceName);
        if (!iconKey) return;
        
        // Priority: mediaPath > file
        if (attachment.mediaPath && typeof attachment.mediaPath === 'string') {
          newWidgetIcons[iconKey] = attachment.mediaPath;
        } else if (attachment.file && Array.isArray(attachment.file) && attachment.file.length > 0) {
          try {
            const uint8Array = new Uint8Array(attachment.file);
            const blob = new Blob([uint8Array], { 
              type: attachment.mimeType || 'image/png' 
            });
            const blobUrl = URL.createObjectURL(blob);
            newWidgetIcons[iconKey] = blobUrl;
            newBlobUrls.add(blobUrl);
            blobUrlsRef.current.add(blobUrl);
          } catch (error) {
            console.error('Error creating blob URL:', error);
          }
        }
      });
    }
    
    setWidgetIcons(newWidgetIcons);
  }, [attachmentList, item?.widgetId, item?.id]);

  // Fetch saved queries based on item type
  useEffect(() => {
    const hasFilters = (selectedBankIds?.length > 0) || (selectedBranchIds?.length > 0);
    
    if (hasFilters) {
      const fetchSavedQueries = async () => {
        try {
          setLoadingQueries(true);
          setQueryError(null);
          console.log("Map: Fetching saved queries for MAP type");
          
          const response = await getData(
            getQueriesListByWidgetType.replace("WIDGETTYPE", "MAP")
          );
          
          console.log("Map: Raw saved queries response:", response);
          
          const parsed = response.map((queryItem) => ({
            ...queryItem,
            queryBuilder:
              typeof queryItem.queryBuilder === "string"
                ? JSON.parse(queryItem.queryBuilder)
                : queryItem.queryBuilder,
          }));

          console.log("Map: Parsed saved queries:", parsed);
          setSavedQueries(parsed);
        } catch (err) {
          console.error("Map: Error fetching saved queries:", err);
          setQueryError("Failed to load saved queries");
        } finally {
          setLoadingQueries(false);
        }
      };

      fetchSavedQueries();
    }
  }, [selectedBankIds, selectedBranchIds]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  // Transform NiFi response to map data format
  const transformNiFiDataToMapFormat = (nifiResponse) => {
    try {
      console.log("Map: Raw NiFi response:", nifiResponse);
      
      if (nifiResponse && nifiResponse.status === 'success' && nifiResponse.data) {
        const { data } = nifiResponse;
        
        // HANDLE MULTIPLE DATA SOURCES FORMAT (from multiple API calls)
        if (typeof data === 'object' && !Array.isArray(data)) {
          console.log("Map: Processing multiple data sources format:", data);
          const points = [];
          
          // Iterate through each data source
          for (const source in data) {
            const sourceData = data[source] || [];
            console.log(`Map: Processing source "${source}" with ${sourceData.length} items:`, sourceData);
            
            if (Array.isArray(sourceData)) {
              sourceData.forEach((dataItem, index) => {
                // Try different possible coordinate field names
                const lat = parseFloat(dataItem.lat || dataItem.latitude || dataItem.Latitude || 
                           dataItem.LAT || dataItem.y || dataItem.Y);
                const lng = parseFloat(dataItem.lng || dataItem.longitude || dataItem.Longitude || 
                           dataItem.LNG || dataItem.x || dataItem.X);

                // Debug coordinate extraction
                console.log(`Map: Source "${source}" item ${index} coordinates:`, {
                  lat, lng, 
                  originalLat: dataItem.lat || dataItem.latitude,
                  originalLng: dataItem.lng || dataItem.longitude,
                  allFields: Object.keys(dataItem)
                });

                // Validate coordinates
                if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                  const pointData = {
                    lat: lat,
                    lng: lng,
                    type: source, // Use the source key as the type
                  };

                  // Add all other fields from the data item
                  Object.keys(dataItem).forEach(key => {
                    const lowerKey = key.toLowerCase();
                    if (!['lat', 'lng', 'latitude', 'longitude', 'x', 'y'].includes(lowerKey)) {
                      pointData[key] = dataItem[key];
                    }
                  });

                  points.push(pointData);
                  console.log(`Map: Added point for source "${source}":`, pointData);
                } else {
                  console.warn(`Map: Invalid coordinates for source "${source}" item ${index}:`, { lat, lng, dataItem });
                }
              });
            } else {
              console.warn(`Map: Source data for "${source}" is not an array:`, sourceData);
            }
          }
          
          console.log(`Map: Total points created from multiple sources: ${points.length}`);
          console.log("Map: All points:", points);
          
          const validatedPoints = validateMapPoints(points);
          return { points: validatedPoints };
        }
        
        // Handle single array format
        if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
          console.log("Map: Data is already in object format:", data);
          
          const points = [];
          
          // If we have fieldsBySource configuration, use it to process the data properly
          if (item.fieldsBySource) {
            // Process each source defined in fieldsBySource
            for (const source in item.fieldsBySource) {
              const fieldsConfig = item.fieldsBySource[source] || [];
              
              // For each data item, create a point for this source
              data.forEach((dataItem) => {
                const pointData = {
                  lat: parseFloat(dataItem.lat || dataItem.latitude || dataItem.Latitude || 25.5941),
                  lng: parseFloat(dataItem.lng || dataItem.longitude || dataItem.Longitude || 85.1376),
                  type: source,
                };

                // Add selected fields from fieldsConfig in the specified order
                fieldsConfig
                  .filter((field) => field.selected)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .forEach((field) => {
                    pointData[field.key] = dataItem[field.key];
                  });

                // Also add any additional important fields that aren't in fieldsConfig
                const importantFields = ['id', 'bank_id', 'branch_id', 'bankId', 'branchId', 'Bank_ID', 'Branch_ID', 'branchdetails_id'];
                importantFields.forEach(fieldKey => {
                  if (dataItem.hasOwnProperty(fieldKey) && !pointData.hasOwnProperty(fieldKey)) {
                    pointData[fieldKey] = dataItem[fieldKey];
                  }
                });

                points.push(pointData);
              });
            }
          } else {
            // Fallback: if no fieldsBySource, treat all data as single source
            const transformedPoints = data.map(dataItem => {
              const lat = dataItem.lat || dataItem.latitude || dataItem.Latitude || 
                         dataItem.LAT || dataItem.y || dataItem.Y;
              const lng = dataItem.lng || dataItem.longitude || dataItem.Longitude || 
                         dataItem.LNG || dataItem.x || dataItem.X;
              
              const type = dataItem.type || dataItem.source || dataItem.category || 
                          dataItem.service || dataItem.serviceName || 'default';

              const pointData = {
                lat: parseFloat(lat) || 25.5941,
                lng: parseFloat(lng) || 85.1376,
                type: type,
              };

              // Add all other fields from the data item (excluding coordinate fields to avoid duplication)
              Object.keys(dataItem).forEach(key => {
                const lowerKey = key.toLowerCase();
                if (!['lat', 'lng', 'latitude', 'longitude', 'x', 'y', 'type', 'source', 'category', 'service', 'servicename'].includes(lowerKey)) {
                  pointData[key] = dataItem[key];
                }
              });

              return pointData;
            });
            
            points.push(...transformedPoints);
          }
          
          const validatedPoints = validateMapPoints(points);
          return { points: validatedPoints };
        }
        
        // If data is array of arrays with columns
        if (nifiResponse.columns && Array.isArray(data) && Array.isArray(data[0])) {
          const { columns } = nifiResponse;
          const transformedData = data.map(row => {
            const dataPoint = {};
            columns.forEach((column, index) => {
              let cleanKey = column
                .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, '')
                .replace(/\)$/, '')
                .replace(/^.*\./, '');
              dataPoint[cleanKey] = row[index];
            });
            return dataPoint;
          });
          
          console.log("Map: Transformed data from array of arrays:", transformedData);
          
          const transformedPoints = transformedData.map(dataItem => {
            const lat = dataItem.lat || dataItem.latitude || dataItem.Latitude || 
                       dataItem.LAT || dataItem.y || dataItem.Y;
            const lng = dataItem.lng || dataItem.longitude || dataItem.Longitude || 
                       dataItem.LNG || dataItem.x || dataItem.X;
            const type = dataItem.type || dataItem.source || dataItem.category || 
                        dataItem.service || dataItem.serviceName || 'default';

            const pointData = {
              lat: lat || 25.5941,
              lng: lng || 85.1376,
              type: type,
            };

            // Add all other fields
            Object.keys(dataItem).forEach(key => {
              const lowerKey = key.toLowerCase();
              if (!['lat', 'lng', 'latitude', 'longitude', 'x', 'y', 'type', 'source', 'category', 'service', 'servicename'].includes(lowerKey)) {
                pointData[key] = dataItem[key];
              }
            });

            return pointData;
          });
          
          const validatedPoints = validateMapPoints(transformedPoints);
          return { points: validatedPoints };
        }
      }
      
      console.log("Map: No valid data found, returning empty points array");
      return { points: [] };
    } catch (error) {
      console.error('Map: Error transforming NiFi data:', error);
      return { points: [] };
    }
  };

  // Helper function to check if query is bank-only (core.bank without joins)
  const isBankOnlyQuery = (queryObject) => {
    const entity = queryObject.entity || "";
    const joins = queryObject.joins || [];
    
    // Check if entity is core.bank and no joins are present
    const isCoreBank = entity.toLowerCase().includes("core.bank") || entity.toLowerCase() === "bank";
    const hasNoJoins = joins.length === 0;
    
    console.log("Map: Checking if bank-only query:", { entity, isCoreBank, hasNoJoins, joins });
    
    return isCoreBank && hasNoJoins;
  };

  // Function to fetch filtered data from NiFi - UPDATED FOR BANK-ONLY SCENARIOS
 // Function to fetch filtered data from NiFi - UPDATED FOR BANK-ONLY SCENARIOS
const fetchFilteredData = async (bankIds, branchIds) => {
  try {
    if (!item?.dataSource || !savedQueries || savedQueries.length === 0) {
      console.log("Map: No data source or saved queries available");
      const originalData = item.data || { points: [] };
      const validatedPoints = validateMapPoints(originalData.points);
      return { ...originalData, points: validatedPoints };
    }

    // Handle multiple data sources
    const dataSources = Array.isArray(item.dataSource) ? item.dataSource : [item.dataSource];
    console.log("Map: Processing data sources:", dataSources);

    // If we have multiple data sources, fetch data for each one
    if (dataSources.length > 1) {
      console.log("Map: Multiple data sources detected, fetching data for each");
      
      const dataSourcePromises = dataSources.map(async (dataSource) => {
        console.log(`Map: Fetching data for data source: ${dataSource}`);
        
        // Find matching query for this specific data source
        // FIXED: Use dashboardWidgetId for primary matching, then fall back to serviceName
        let matchedQuery = savedQueries.find(
          (q) => 
            q.dashboardWidgetId === `WIDGET_${item.id}` &&
            q.serviceName === dataSource && 
            q.queryStage === "SECONDARY" && 
            q.activeStatus === true
        );

        // Fallback: try matching by serviceName only if no widget ID + serviceName match
        if (!matchedQuery) {
          matchedQuery = savedQueries.find(
            (q) => q.serviceName === dataSource && q.queryStage === "SECONDARY" && q.activeStatus === true
          );
          
          if (matchedQuery) {
            console.log(`Map: Using fallback match for data source ${dataSource} on widget ${item.id}`);
          }
        }

        if (!matchedQuery) {
          console.warn(`Map: No matching query found for data source: ${dataSource}`);
          return { dataSource, data: [] };
        }

        console.log(`Map: Found matching query for ${dataSource}:`, matchedQuery);

        // Get query object
        const queryObject =
          typeof matchedQuery.queryBuilder === "string"
            ? JSON.parse(matchedQuery.queryBuilder)
            : matchedQuery.queryBuilder;

        // Check if this is a bank-only query
        const isBankOnly = isBankOnlyQuery(queryObject);
        console.log(`Map: Data source ${dataSource} - Is bank-only query:`, isBankOnly);

        if (isBankOnly) {
          // BANK-ONLY SCENARIOS
          const hasBankIds = bankIds && bankIds.length > 0;
          const hasBranchIds = branchIds && branchIds.length > 0;

          console.log(`Map: Bank-only query scenarios for ${dataSource}:`, {
            hasBankIds,
            hasBranchIds,
            bankIds,
            branchIds
          });

          // Scenario 2: Only branch IDs are available (no bank IDs)
          if (!hasBankIds && hasBranchIds) {
            console.log(`Map: Scenario 2 - Only branch IDs available for bank-only query ${dataSource}, returning empty data`);
            return { dataSource, data: [] };
          }

          // Scenario 1: Only bank IDs are available
          // Scenario 3: Both bank and branch IDs are available (use only bank IDs)
          if (hasBankIds) {
            console.log(`Map: Scenario ${hasBranchIds ? '3' : '1'} - Using bank IDs for bank-only query ${dataSource}`);
            
            const filters = {
              "bank.id": {
                operator: "IN",
                value: bankIds
              }
            };

            const enhancedQuery = {
              ...queryObject,
              filters: {
                ...(queryObject.filters || {}),
                ...filters,
              },
            };

            console.log(`Map: Enhanced bank-only query for ${dataSource}:`, JSON.stringify(enhancedQuery, null, 2));

            // Make API call
            const response = await fetch("/nifi/fetchQuery", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(enhancedQuery),
            });

            if (!response.ok) {
              throw new Error(`HTTP error for ${dataSource}! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Map: NiFi Query Result for bank-only ${dataSource}:`, result);

            if (result.status === "error") {
              throw new Error(result.message || `Query execution failed for ${dataSource}`);
            }

            return { 
              dataSource, 
              data: result.data || [] 
            };
          }

          // No bank IDs and no branch IDs - return empty
          console.log(`Map: No bank IDs available for bank-only query ${dataSource}, returning empty data`);
          return { dataSource, data: [] };
        } else {
          // NON-BANK-ONLY QUERIES (existing logic)
          // Extract table name from entity
          const entity = queryObject.entity || "";
          const tableName = entity.includes(".") ? entity.split(".")[1] : entity;
          
          // Get joins information
          const joins = queryObject.joins || [];
          const joinedTables = joins.map(join => {
            const table = join.table || join.alias || "";
            return table.includes(".") ? table.split(".")[1] : table;
          });

          const determineFilterFields = () => {
            let bankFilterField = null;
            let branchFilterField = null;

            console.log(`Map: Table analysis for ${dataSource}:`, { tableName, joinedTables });

            // FIRST CHECK: For any table that is NOT "bank" or "branch_details", use tableName.bank_id and tableName.branch_id
            if (tableName !== "bank" && tableName !== "branch_details") {
              bankFilterField = `${tableName}.bank_id`;
              branchFilterField = `${tableName}.branch_id`;
              return { bankFilterField, branchFilterField };
            }

            // If the first condition doesn't match, proceed with existing join logic
            const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
            const hasBranchDetailsTable = tableName === "branch_details" || joinedTables.includes("branch_details");
            const hasJoins = joins.length > 0;

            // Determine bank filter field
            if (tableName === "bank") {
              bankFilterField = "bank.id";
              if (!hasJoins) {
                branchFilterField = null;
                return { bankFilterField, branchFilterField };
              }
            } else if (hasBankTable && hasJoins) {
              bankFilterField = "bank.id";
            } else if (!hasBankTable && tableName !== "bank" && tableName !== "branch_details") {
              bankFilterField = `${tableName}.bank_id`;
            } else if (!hasBankTable) {
              bankFilterField = `${tableName}.bank_id`;
            }

            // Determine branch filter field
            if (tableName === "branch_details") {
              branchFilterField = "branch_details.id";
            } else if (hasBranchDetailsTable && hasJoins) {
              branchFilterField = "branch_details.id";
            } else if (!hasBranchDetailsTable && tableName !== "bank" && tableName !== "branch_details") {
              branchFilterField = `${tableName}.branch_id`;
            } else {
              const tableWithBranchId = joinedTables.find(table => table !== "bank" && table !== "branch_details");
              if (tableWithBranchId) {
                branchFilterField = `${tableWithBranchId}.branch_id`;
              } else {
                branchFilterField = `${tableName}.branch_id`;
              }
            }

            return { bankFilterField, branchFilterField };
          };

          const { bankFilterField, branchFilterField } = determineFilterFields();
          
          console.log(`Map: Filter fields for ${dataSource}:`, { bankFilterField, branchFilterField });

          const filters = {};

          // Add bank filter if bankIds provided and field determined
          if (bankIds && bankIds.length > 0 && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: bankIds
            };
          }

          // Add branch filter if branchIds provided and field determined
          if (branchIds && branchIds.length > 0 && branchFilterField) {
            filters[branchFilterField] = {
              operator: "IN", 
              value: branchIds
            };
          } else if (!branchIds || branchIds.length === 0) {
            if (branchFilterField) {
              filters[branchFilterField] = null;
            }
          }

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(`Map: Enhanced query for ${dataSource}:`, JSON.stringify(enhancedQuery, null, 2));

          // Make API call for this specific data source
          const response = await fetch("/nifi/fetchQuery", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(enhancedQuery),
          });

          if (!response.ok) {
            throw new Error(`HTTP error for ${dataSource}! status: ${response.status}`);
          }

          const result = await response.json();
          console.log(`Map: NiFi Query Result for ${dataSource}:`, result);

          if (result.status === "error") {
            throw new Error(result.message || `Query execution failed for ${dataSource}`);
          }

          return { 
            dataSource, 
            data: result.data || [] 
          };
        }
      });

      // Wait for all data source promises to complete
      const dataSourceResults = await Promise.all(dataSourcePromises);
      
      // Combine results into the expected format for maps
      const combinedData = {};
      dataSourceResults.forEach(result => {
        combinedData[result.dataSource] = result.data;
      });

      console.log("Map: Combined data from multiple sources:", combinedData);

      // Transform combined data to map format
      const transformedData = transformNiFiDataToMapFormat({ 
        status: 'success', 
        data: combinedData 
      });
      
      console.log("Map: Final transformed data from multiple sources:", transformedData);
      return transformedData;
    } 
    
    // SINGLE DATA SOURCE LOGIC
    else {
      const singleDataSource = dataSources[0];
      console.log("Map: Single data source detected:", singleDataSource);

      // Try different widget ID patterns to find a match
      const possibleWidgetIds = [
        `WIDGET_${item.id}`,
        `widget_${item.id}`,
        `${item.id}`,
        singleDataSource,
      ];

      let matchedQuery = null;
      for (const widgetId of possibleWidgetIds) {
        matchedQuery = savedQueries.find(
          (q) => q.dashboardWidgetId === widgetId
        );
        if (matchedQuery) {
          console.log(`Map: Found matching query with widgetId: ${widgetId}`);
          break;
        }
      }
      
      // Fallback: try matching by serviceName
      if (!matchedQuery) {
        matchedQuery = savedQueries.find(
          (q) => q.serviceName === singleDataSource && q.queryStage === "SECONDARY" && q.activeStatus === true
        );
        
        if (matchedQuery) {
          console.log(`Map: Found matching query by serviceName: ${singleDataSource}`);
        }
      }
      
      if (!matchedQuery) {
        console.log("Map: No matching query found for any widget ID pattern or serviceName");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        return { ...originalData, points: validatedPoints };
      }

      // Get query object
      const queryObject =
        typeof matchedQuery.queryBuilder === "string"
          ? JSON.parse(matchedQuery.queryBuilder)
          : matchedQuery.queryBuilder;

      // Check if this is a bank-only query
      const isBankOnly = isBankOnlyQuery(queryObject);
      console.log(`Map: Single data source ${singleDataSource} - Is bank-only query:`, isBankOnly);

      if (isBankOnly) {
        // BANK-ONLY SCENARIOS FOR SINGLE DATA SOURCE
        const hasBankIds = bankIds && bankIds.length > 0;
        const hasBranchIds = branchIds && branchIds.length > 0;

        console.log(`Map: Bank-only query scenarios for single source ${singleDataSource}:`, {
          hasBankIds,
          hasBranchIds,
          bankIds,
          branchIds
        });

        // Scenario 2: Only branch IDs are available (no bank IDs)
        if (!hasBankIds && hasBranchIds) {
          console.log(`Map: Scenario 2 - Only branch IDs available for bank-only query, clearing data`);
          return { points: [] };
        }

        // Scenario 1: Only bank IDs are available
        // Scenario 3: Both bank and branch IDs are available (use only bank IDs)
        if (hasBankIds) {
          console.log(`Map: Scenario ${hasBranchIds ? '3' : '1'} - Using bank IDs for bank-only query`);
          
          const filters = {
            "bank.id": {
              operator: "IN",
              value: bankIds
            }
          };

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(`Map: Enhanced bank-only query for single source:`, JSON.stringify(enhancedQuery, null, 2));

          // Make API call
          const response = await fetch("/nifi/fetchQuery", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(enhancedQuery),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log(`Map: NiFi Query Result for bank-only single source:`, result);

          if (result.status === "error") {
            throw new Error(result.message || `Query execution failed`);
          }

          // Transform the result
          const transformedData = transformNiFiDataToMapFormat(result);
          console.log("Map: Final transformed data from bank-only single source:", transformedData);
          return transformedData;
        }

        // No bank IDs and no branch IDs - return empty
        console.log(`Map: No bank IDs available for bank-only query, returning empty data`);
        return { points: [] };
      } else {
        // NON-BANK-ONLY SINGLE SOURCE QUERIES (existing logic)
        console.log("Map: Processing non-bank-only single source query");
        
        // Extract table name from entity
        const entity = queryObject.entity || "";
        const tableName = entity.includes(".") ? entity.split(".")[1] : entity;
        
        // Get joins information
        const joins = queryObject.joins || [];
        const joinedTables = joins.map(join => {
          const table = join.table || join.alias || "";
          return table.includes(".") ? table.split(".")[1] : table;
        });

        const determineFilterFields = () => {
          let bankFilterField = null;
          let branchFilterField = null;

          console.log(`Map: Table analysis for single source ${singleDataSource}:`, { tableName, joinedTables });

          // FIRST CHECK: For any table that is NOT "bank" or "branch_details", use tableName.bank_id and tableName.branch_id
          if (tableName !== "bank" && tableName !== "branch_details") {
            bankFilterField = `${tableName}.bank_id`;
            branchFilterField = `${tableName}.branch_id`;
            return { bankFilterField, branchFilterField };
          }

          // If the first condition doesn't match, proceed with existing join logic
          const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
          const hasBranchDetailsTable = tableName === "branch_details" || joinedTables.includes("branch_details");
          const hasJoins = joins.length > 0;

          // Determine bank filter field
          if (tableName === "bank") {
            bankFilterField = "bank.id";
            if (!hasJoins) {
              branchFilterField = null;
              return { bankFilterField, branchFilterField };
            }
          } else if (hasBankTable && hasJoins) {
            bankFilterField = "bank.id";
          } else if (!hasBankTable && tableName !== "bank" && tableName !== "branch_details") {
            bankFilterField = `${tableName}.bank_id`;
          } else if (!hasBankTable) {
            bankFilterField = `${tableName}.bank_id`;
          }

          // Determine branch filter field
          if (tableName === "branch_details") {
            branchFilterField = "branch_details.id";
          } else if (hasBranchDetailsTable && hasJoins) {
            branchFilterField = "branch_details.id";
          } else if (!hasBranchDetailsTable && tableName !== "bank" && tableName !== "branch_details") {
            branchFilterField = `${tableName}.branch_id`;
          } else {
            const tableWithBranchId = joinedTables.find(table => table !== "bank" && table !== "branch_details");
            if (tableWithBranchId) {
              branchFilterField = `${tableWithBranchId}.branch_id`;
            } else {
              branchFilterField = `${tableName}.branch_id`;
            }
          }

          return { bankFilterField, branchFilterField };
        };

        const { bankFilterField, branchFilterField } = determineFilterFields();
        
        console.log(`Map: Filter fields for single source ${singleDataSource}:`, { bankFilterField, branchFilterField });

        const filters = {};

        // Add bank filter if bankIds provided and field determined
        if (bankIds && bankIds.length > 0 && bankFilterField) {
          filters[bankFilterField] = {
            operator: "IN",
            value: bankIds
          };
        }

        // Add branch filter if branchIds provided and field determined
        if (branchIds && branchIds.length > 0 && branchFilterField) {
          filters[branchFilterField] = {
            operator: "IN", 
            value: branchIds
          };
        } else if (!branchIds || branchIds.length === 0) {
          if (branchFilterField) {
            filters[branchFilterField] = null;
          }
        }

        const enhancedQuery = {
          ...queryObject,
          filters: {
            ...(queryObject.filters || {}),
            ...filters,
          },
        };

        console.log(`Map: Enhanced non-bank-only query for single source:`, JSON.stringify(enhancedQuery, null, 2));

        // Make API call
        const response = await fetch("/nifi/fetchQuery", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(enhancedQuery),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log(`Map: NiFi Query Result for non-bank-only single source:`, result);

        if (result.status === "error") {
          throw new Error(result.message || `Query execution failed`);
        }

        // Transform the result
        const transformedData = transformNiFiDataToMapFormat(result);
        console.log("Map: Final transformed data from non-bank-only single source:", transformedData);
        return transformedData;
      }
    }
  } catch (error) {
    console.error("Map: Error fetching filtered data:", error);
    const originalData = item.data || { points: [] };
    const validatedPoints = validateMapPoints(originalData.points);
    return { ...originalData, points: validatedPoints };
  }
};


  // Effect to handle data fetching when filters change
  useEffect(() => {
    const loadData = async () => {
      console.log("Map: LoadData called with:", {
        selectedBankIds,
        selectedBranchIds,
        hasDataSource: !!item?.dataSource,
        savedQueriesLength: savedQueries.length,
        loadingQueries,
        itemId: item.id
      });

      // Check if any filters are applied
      const hasFilters = (selectedBankIds && selectedBankIds.length > 0) || 
                        (selectedBranchIds && selectedBranchIds.length > 0);

      if (hasFilters) {
        setLoading(true);
        try {
          console.log("Map: Filters applied, attempting to fetch filtered data");
          
          const fetchedData = await fetchFilteredData(
            selectedBankIds,
            selectedBranchIds
          );
          
          console.log("Map: Setting map data to:", fetchedData);
          setMapData({ ...fetchedData });
          
        } catch (error) {
          console.error("Map: Error loading filtered data:", error);
          const originalData = item.data || { points: [] };
          const validatedPoints = validateMapPoints(originalData.points);
          setMapData({ ...originalData, points: validatedPoints });
        } finally {
          setLoading(false);
        }
      } else {
        console.log("Map: No filters applied, using original data");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        setMapData({ ...originalData, points: validatedPoints });
      }
    };

    // Only run loadData if queries have finished loading
    if (!loadingQueries) {
      loadData();
    }
  }, [
    selectedBankIds,
    selectedBranchIds,
    item.dataSource,
    loadingQueries,
    savedQueries.length,
    item.id
  ]);

  // REMOVED CLIENT-SIDE FILTERING - Use mapData directly
  const filteredItem = {
    ...item,
    data: mapData, // Use mapData directly without any client-side filtering
  };

  console.log("Map: Final filteredItem data:", filteredItem.data);

  // Base layers configuration
  const baseLayers = {
    Standard: L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { attribution: "" }
    ),
    Satellite: L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    ),
    Terrain: L.tileLayer(
      "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
    ),
    Hybrid: L.tileLayer(
      "https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
    ),
    Streets: L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      {
      maxNativeZoom: 17
    }
    ),
    Road: L.tileLayer(
      "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
    ),
  };

  const getDefaultIcon = () => {
    return iconMap?.default?.options?.iconUrl || 
           "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjM0I4MkY2Ci8+PC9zdmc+";
  };

  // Get icon for source with proper fallback chain
  const getIconForSource = (sourceName) => {
    const size = iconSizes?.[sourceName] || [32, 32];
    const iconKey = getWidgetIconKey(sourceName);
    
    let iconUrl = null;
    
    // Priority 1: Widget-specific icons (from attachments)
    if (iconKey && widgetIcons[iconKey]) {
      iconUrl = widgetIcons[iconKey];
    } 
    // Priority 2: Global uploaded icons (current session)
    else if (iconKey && uploadedIcons && uploadedIcons[iconKey]) {
      iconUrl = uploadedIcons[iconKey];
    } 
    // Priority 3: Icon map fallback
    else if (iconMap && iconMap[sourceName] && iconMap[sourceName].options && iconMap[sourceName].options.iconUrl) {
      iconUrl = iconMap[sourceName].options.iconUrl;
    } 
    // Priority 4: Default
    else {
      iconUrl = getDefaultIcon();
    }

    return new L.Icon({
      iconUrl: iconUrl,
      iconSize: size,
      iconAnchor: [size[0] / 2, size[1]],
      popupAnchor: [0, -size[1]],
    });
  };

  // Get icon URL for control with widget-specific context
  const getIconUrlForControl = (sourceName) => {
    const iconKey = getWidgetIconKey(sourceName);
    
    if (iconKey && widgetIcons[iconKey]) {
      return widgetIcons[iconKey];
    }
    
    if (iconKey && uploadedIcons && uploadedIcons[iconKey]) {
      return uploadedIcons[iconKey];
    }
    
    if (iconMap && iconMap[sourceName] && iconMap[sourceName].options && iconMap[sourceName].options.iconUrl) {
      return iconMap[sourceName].options.iconUrl;
    }
    
    return getDefaultIcon();
  };

  // Show loading state for queries or data
  if (loading || loadingQueries) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        {loadingQueries ? "Loading queries..." : "Loading map data..."}
      </div>
    );
  }

  // Show error state if queries failed to load
  if (queryError) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #ef4444",
          }}
        >
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            Error Loading Queries
          </div>
          <div style={{ fontSize: "12px" }}>{queryError}</div>
        </div>
      </div>
    );
  }

  // Show no data message when filters are applied but no data matches
  if (
    mapData.points?.length === 0 &&
    ((selectedBankIds && selectedBankIds.length > 0) ||
      (selectedBranchIds && selectedBranchIds.length > 0))
  ) {
    const hasSelectedBranches = selectedBranchIds && selectedBranchIds.length > 0;
    const hasSelectedBanks = selectedBankIds && selectedBankIds.length > 0;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            No Data Available
          </div>
          <div style={{ color: "#d97706", fontSize: "12px" }}>
            {hasSelectedBranches
              ? `No data found for the selected branch${
                  selectedBranchIds.length > 1 ? "es" : ""
                }`
              : hasSelectedBanks
              ? `No data found for the selected bank${
                  selectedBankIds.length > 1 ? "s" : ""
                }`
              : `No data found for the current selection`}
          </div>
        </div>
      </div>
    );
  }

  const MapMarkers = () => {
    const map = useMap();
    
    useEffect(() => {
      if (!map) return;

      console.log("Map: MapMarkers effect triggered");
      console.log("Map: filteredItem.data:", filteredItem.data);
      console.log("Map: filteredItem.dataSource:", filteredItem.dataSource);

      // Replace the handleResize function
const handleResize = () => {
  if (map && map.getContainer() && map._mapPane) {
    try {
      map.invalidateSize();
    } catch (error) {
      console.warn('Map invalidateSize failed:', error);
    }
  }
};

      handleResize();
      window.addEventListener("resize", handleResize);
      window.leafletMap = map;

      // Clear existing layers
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer);
        }
      });

      baseLayers.Standard.addTo(map);

      const overlayLayers = {};
      const selectedSources = Array.isArray(filteredItem.dataSource)
        ? filteredItem.dataSource
        : filteredItem.dataSource
        ? [filteredItem.dataSource]
        : [];

      console.log("Map: Selected sources for rendering:", selectedSources);
      console.log("Map: Total points available:", filteredItem.data.points?.length || 0);

      // Group points by source for debugging
      const pointsBySource = {};
      if (filteredItem.data.points) {
        filteredItem.data.points.forEach(point => {
          const source = point.type || 'unknown';
          if (!pointsBySource[source]) {
            pointsBySource[source] = [];
          }
          pointsBySource[source].push(point);
        });
      }
      
      console.log("Map: Points grouped by source:", pointsBySource);

      selectedSources.forEach((source) => {
        console.log(`Map: Creating layer for source: ${source}`);
        
        const group = L.layerGroup();
        const sourcePoints = filteredItem.data.points.filter((point) => point.type === source);
        
        console.log(`Map: Found ${sourcePoints.length} points for source "${source}":`, sourcePoints);
        
        sourcePoints.forEach((point, index) => {
          // Validate coordinates before creating marker
          const lat = parseFloat(point.lat);
          const lng = parseFloat(point.lng);
          
          console.log(`Map: Processing point ${index} for source "${source}":`, { lat, lng, point });
          
          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn(`Map: Skipping marker ${index} for source "${source}" with invalid coordinates:`, { lat, lng, point });
            return;
          }

          const fieldsConfig = filteredItem.fieldsBySource?.[source] || [];
          const excludedFields = [
            "bank_id", "bankId", "Bank_ID", "branch_id", 
            "branchId", "Branch_ID", "branchdetails_id",
          ];

          const fieldsToDisplay = fieldsConfig
            .filter((field) => field.selected)
            .filter((field) => !excludedFields.includes(field.key))
            .sort((a, b) => (a.order || 0) - (b.order || 0));

          console.log(`Map: Fields to display for source "${source}":`, fieldsToDisplay);

          const currentIcon = getIconForSource(source);
          const iconUrlForPopup = getIconUrlForControl(source);

          const popupContent = `
  <div style="
    max-height: 180px; overflow-y: auto; padding: 10px; border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); background: white;
    width: 220px; font-family: sans-serif;
  ">
    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
      <img src="${iconUrlForPopup}" width="20" height="20" style="object-fit: contain;" 
           onerror="this.src='${getDefaultIcon()}'" />
      <span style="font-weight: 700; font-size: 16px; color: #1a7a73;">${source}</span>
    </div>
    <div style="display: grid; grid-template-columns: auto 1fr; gap: 6px; font-size: 14px;">
      ${fieldsToDisplay
        .map((field) => {
          // Handle coordinate field display with fallback
          let value = point[field.key];
          
          // Special handling for coordinate fields
          if (['lat', 'latitude', 'Latitude'].includes(field.key)) {
            value = point.lat || point.latitude || point.Latitude || "N/A";
          } else if (['lng', 'longitude', 'Longitude'].includes(field.key)) {
            value = point.lng || point.longitude || point.Longitude || "N/A";
          }
          
          return `
            <div style="font-weight: 600; color: #4b5563;">${field.label || field.key}:</div>
            <div style="color: #1f2937;">${value || "N/A"}</div>
          `;
        })
        .join("")}
    </div>
    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
      Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}
    </div>
  </div>
`;


          const marker = L.marker([lat, lng], {
            icon: currentIcon,
          }).bindPopup(popupContent);
          
          marker.addTo(group);
          console.log(`Map: Added marker ${index} for source "${source}" at [${lat}, ${lng}]`);
        });

        overlayLayers[source] = group;
        console.log(`Map: Created overlay layer for source "${source}" with ${sourcePoints.length} markers`);
      });

      console.log("Map: All overlay layers created:", Object.keys(overlayLayers));

      const control = L.control
        .layers(baseLayers, overlayLayers, {
          position: "topright",
          collapsed: false,
        })
        .addTo(map);

      // Add all overlay layers to map and ensure they're visible
      Object.entries(overlayLayers).forEach(([sourceName, layer]) => {
        layer.addTo(map);
        console.log(`Map: Added overlay layer "${sourceName}" to map`);
      });

      // Use widget-specific localStorage key for base layer selection
      const widgetId = getWidgetId();
      const baseLayerStorageKey = `selectedBaseLayer_widget_${widgetId}`;

      map.on("baselayerchange", function (e) {
        localStorage.setItem(baseLayerStorageKey, e.name);
      });

      // Load saved base layer specific to this widget
      const savedBaseLayer = localStorage.getItem(baseLayerStorageKey);
      if (savedBaseLayer && baseLayers[savedBaseLayer]) {
        map.eachLayer((layer) => {
          if (layer instanceof L.TileLayer) {
            map.removeLayer(layer);
          }
        });
        baseLayers[savedBaseLayer].addTo(map);
      }

      const geocoderControl = L.Control.geocoder({
        defaultMarkGeocode: false,
        position: "topleft",
      })
        .on("markgeocode", function (e) {
          const { center, html } = e.geocode;
          
          const searchMarker = L.marker(center).addTo(map);
          
          const removeMarker = () => {
            map.removeLayer(searchMarker);
          };
          
          const popupContent = document.createElement('div');
          popupContent.style.cssText = `
            padding: 10px; border-radius: 8px;
            background: white; width: 200px; font-family: sans-serif;
          `;
          
          popupContent.innerHTML = `
            <div style="margin-bottom: 10px;">
              <div style="font-weight: 600; color: #1a7a73; margin-bottom: 5px;">📍 Searched Location</div>
              <div style="font-size: 14px; color: #1f2937;">${html}</div>
            </div>
            <button 
              id="removeBtn"
              style="
                background: #ef4444; color: white; border: none; padding: 6px 12px;
                border-radius: 4px; cursor: pointer; font-size: 12px;
                width: 100%; transition: background 0.2s;
              "
            >
               Remove Location
            </button>
          `;
          
          const removeBtn = popupContent.querySelector('#removeBtn');
          removeBtn.addEventListener('click', removeMarker);
          removeBtn.addEventListener('mouseover', function() {
            this.style.background = '#dc2626';
          });
          removeBtn.addEventListener('mouseout', function() {
            this.style.background = '#ef4444';
          });
          
          searchMarker.bindPopup(popupContent);
          map.setView(center, 14);
          searchMarker.openPopup();
        })
        .addTo(map);

      setTimeout(() => {
        const geocoderEl = document.querySelector(".leaflet-control-geocoder");
        if (geocoderEl) geocoderEl.style.marginTop = "10px";
      }, 0);

      // Insert control icons with proper widget-specific context
      const insertControlIcons = () => {
        const currentWidgetId = getWidgetId();
        const mapContainer = map.getContainer();
        const labels = mapContainer.querySelectorAll(
          ".leaflet-control-layers-overlays label"
        );
        
        labels.forEach((label) => {
          const source = label.textContent.trim();
          if (label.querySelector("img")) {
            return;
          }
          
          const currentIconKey = currentWidgetId ? `widget_${currentWidgetId}_service_${source}` : null;
          let iconUrl = null;
          
          if (currentIconKey && widgetIcons[currentIconKey]) {
            iconUrl = widgetIcons[currentIconKey];
          } 
          else if (currentIconKey && uploadedIcons && uploadedIcons[currentIconKey]) {
            iconUrl = uploadedIcons[currentIconKey];
          } 
          else if (iconMap && iconMap[source] && iconMap[source].options && iconMap[source].options.iconUrl) {
            iconUrl = iconMap[source].options.iconUrl;
          } 
          else {
            iconUrl = getDefaultIcon();
          }

          if (iconUrl) {
            const img = document.createElement("img");
            img.src = iconUrl;
            img.style.width = "18px";
            img.style.height = "18px";
            img.style.marginRight = "10px";
            img.style.verticalAlign = "middle";
            img.style.objectFit = "contain";
            
            img.onerror = function() {
              this.src = getDefaultIcon();
            };
            
            label.insertBefore(img, label.firstChild.nextSibling);
          }
        });
      };

      // Add observer to watch for control changes and re-insert icons
      const observeControlChanges = () => {
        const mapContainer = map.getContainer();
        const controlContainer = mapContainer.querySelector('.leaflet-control-layers');
        
        if (controlContainer) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'childList') {
                setTimeout(() => insertControlIcons(), 50);
              }
            });
          });
          
          observer.observe(controlContainer, {
            childList: true,
            subtree: true
          });
          
          map._iconObserver = observer;
        }
      };

      setTimeout(insertControlIcons, 100);
      setTimeout(insertControlIcons, 500);
      setTimeout(insertControlIcons, 1000);
      setTimeout(insertControlIcons, 1500);
      setTimeout(observeControlChanges, 200);

      // FIT BOUNDS WITH VALIDATION
      if (filteredItem.data.points?.length > 0) {
        try {
          const validCoords = filteredItem.data.points
            .map(p => {
              const lat = parseFloat(p.lat);
              const lng = parseFloat(p.lng);
              
              if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                return null;
              }
              
              return [lat, lng];
            })
            .filter(coord => coord !== null);
          
          if (validCoords.length > 0) {
            const bounds = L.latLngBounds(validCoords);
            map.fitBounds(bounds, { padding: [50, 50] });
          } else {
            map.setView([25.5941, 85.1376], 13);
          }
        } catch (error) {
          console.error("Map: Error fitting bounds:", error);
          map.setView([25.5941, 85.1376], 13);
        }
      } else {
        map.setView([25.5941, 85.1376], 13);
      }

      return () => {
        window.removeEventListener("resize", handleResize);
        map.off("baselayerchange");
        map.off("overlayadd");
        map.off("overlayremove");
        
        if (map._iconObserver) {
          map._iconObserver.disconnect();
          delete map._iconObserver;
        }
        
        map.removeControl(control);
        map.removeControl(geocoderControl);
        Object.values(overlayLayers).forEach((layer) =>
          map.removeLayer(layer)
        );
        Object.values(baseLayers).forEach((layer) => {
          if (map.hasLayer(layer)) {
            map.removeLayer(layer);
          }
        });
      };
    }, [
      filteredItem.data,
      filteredItem.dataSource,
      filteredItem.fieldsBySource,
      widgetIcons,
      uploadedIcons,
      iconSizes,
    ]);

    return null;
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        minHeight: "400px",
      }}
    >
      <MapContainer
        center={filteredItem.center || [25.5941, 85.1376]}
        zoom={filteredItem.zoom || 13}
        attributionControl={false}
        maxZoom={18}
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "8px",
          position: "absolute",
        }}
      >
        <MapMarkers />
      </MapContainer>

      <style>{`
        .map-legend-horizontal {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          z-index: 1000;
          display: flex;
          gap: 15px;
          align-items: center;
          font-size: 12px;
        }
        .map-legend-horizontal span {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .leaflet-control-layers-overlays label img {
          object-fit: contain !important;
          max-width: 18px !important;
          max-height: 18px !important;
        }
      `}</style>
    </div>
  );
};

export default Map;